import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
  Divider,
  Collapse,
  IconButton,
  TextField,
  Popover,
  InputAdornment,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WifiIcon from '@mui/icons-material/Wifi';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { api } from '../services/api';
import { 
  computeProjectStats, 
  classifyHealth,
  default as ProjectsNetworkStatusMapView,
  getAllDeviceTypes,
  normalizeDeviceTypeName,
  isSmartDeviceType,
} from './ProjectsNetworkStatusMapView';
import ProjectDetailsDialog from './ProjectDetailsDialog';
import { NEUTRAL, STATUS, CARD_SHADOW, CARD_SHADOW_HOVER, textFor, statusTint } from '../theme';


const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

const formatUtcTime = (date) => {
  if (!date) return '--:--:-- UTC';
  return date.toLocaleTimeString('en-US', { hour12: false, timeZone: 'UTC' }) + ' UTC';
};

const StatusChip = ({ statusKey, label }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 22,
      px: 1,
      borderRadius: 999,
      flexShrink: 0,
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      color: 'text.secondary',
      bgcolor: statusKey === 'critical' ? statusTint(STATUS.critical, 0.28) : NEUTRAL[100],
    }}
  >
    {label}
  </Box>
);

const ProgressBar = ({ value }) => (
  <Box sx={{ height: 3, borderRadius: 999, bgcolor: NEUTRAL[100], overflow: 'hidden' }}>
    <Box sx={{ width: `${Math.max(0, Math.min(100, value))}%`, height: '100%', bgcolor: NEUTRAL[400], borderRadius: 999 }} />
  </Box>
);

const DeviceRow = ({ label, connected, total }) => {
  const offline = Math.max(0, total - connected);
  const pct = total > 0 ? (connected / total) * 100 : 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75, gap: 1 }}>
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {offline > 0 && (
            <Box component="span" sx={{ mr: 1 }}>{formatNumber(offline)} offline</Box>
          )}
          {formatNumber(connected)}
          <Box component="span" sx={{ color: 'text.disabled' }}> / {formatNumber(total)}</Box>
        </Typography>
      </Box>
      <ProgressBar value={pct} />
    </Box>
  );
};

const BreakdownList = ({ rows, emptyLabel }) => (
  rows.length === 0 ? (
    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{emptyLabel}</Typography>
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 240, overflowY: 'auto' }}>
      {rows.map((item) => (
        <Box key={item.type} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '0.8125rem' }}>
          <Box component="span" sx={{ color: 'text.secondary' }}>{item.type}</Box>
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {item.disconnected > 0 && (
              <Box component="span" sx={{ color: textFor(STATUS.critical), mr: 1 }}>{formatNumber(item.disconnected)} offline</Box>
            )}
            {formatNumber(item.connected)}
            <Box component="span" sx={{ color: 'text.disabled' }}> / {formatNumber(item.connected + item.disconnected)}</Box>
          </Box>
        </Box>
      ))}
    </Box>
  )
);

const mapHealthToUiStatus = (health) => {
  const status = health?.status || 'unknown';
  if (status === 'critical') return { key: 'critical', label: 'Critical', color: STATUS.critical };
  if (status === 'degraded') return { key: 'warning', label: 'Warning', color: STATUS.warning };
  if (status === 'healthy') return { key: 'healthy', label: 'Healthy', color: STATUS.healthy };
  return { key: 'warning', label: health?.label || 'Warning', color: STATUS.warning };
};

const summarizeProject = (projectId, project) => {
  const ui = mapHealthToUiStatus(classifyHealth(computeProjectStats(project)));
  const { smart, nonSmart } = getAllDeviceTypes(project);
  const sum = (list, key) => list.reduce((acc, d) => acc + (d[key] || 0), 0);
  const smartConnected = sum(smart, 'connected');
  const smartTotal = sum(smart, 'total');
  const nonSmartConnected = sum(nonSmart, 'connected');
  const nonSmartTotal = sum(nonSmart, 'total');

  const metrics = project.network_quality_metrics || {};
  const topology = project.topology_metrics || {};
  const latencyMs = metrics.avg_latency;
  const latencyQuality = metrics.avg_latency_quality;
  const latency = typeof latencyMs === 'number'
    ? `${latencyMs.toFixed(1)} ms`
    : (typeof latencyQuality === 'number' ? latencyQuality.toFixed(2) : null);
  const packetLossRaw = metrics.avg_packet_loss_pct ?? metrics.avg_packet_loss ?? metrics.packet_loss_percent;
  const packetLoss = packetLossRaw !== null && packetLossRaw !== undefined ? `${Number(packetLossRaw).toFixed(2)}%` : null;
  const hopsRaw = topology.max_tree_depth_connected ?? topology.max_tree_depth ?? topology.max_hops;
  const hops = hopsRaw !== null && hopsRaw !== undefined ? String(hopsRaw) : null;

  const total = smartTotal + nonSmartTotal;
  const online = smartConnected + nonSmartConnected;
  return {
    id: projectId,
    name: project.project_id || projectId,
    ui,
    smartConnected,
    smartTotal,
    smartOffline: Math.max(0, smartTotal - smartConnected),
    nonSmartConnected,
    nonSmartTotal,
    total,
    onlinePct: total > 0 ? (online / total) * 100 : null,
    latency,
    packetLoss,
    hops,
  };
};

const formatPct = (v) => (v === null ? '—' : `${v >= 99.95 || v === 0 ? v.toFixed(0) : v.toFixed(1)}%`);

const SectionHeader = ({ title, count, caption }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{title}</Typography>
    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{count}</Typography>
    {caption && (
      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', ml: 'auto' }}>{caption}</Typography>
    )}
  </Box>
);

// Quieter than STATUS: the header bars sit on white, where the pastel greens read as neon.
const HEADER_BAR = {
  healthy: '#C3CFC8',
  critical: '#DEC4C4',
};

const StatTile = ({ icon: Icon, label, value, detail, segments, action }) => {
  const total = (segments || []).reduce((sum, seg) => sum + seg.value, 0);
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2 }, display: 'flex', gap: 1.75 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: NEUTRAL[100],
            color: NEUTRAL[600],
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {label}
            </Typography>
            {action}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
              {value}
            </Typography>
            {detail}
          </Box>
          {segments && (
            <Box sx={{ display: 'flex', gap: '3px', height: 6, mt: 1.5, borderRadius: 999, overflow: 'hidden' }}>
              {total === 0 ? (
                <Box sx={{ flex: 1, bgcolor: NEUTRAL[100] }} />
              ) : (
                segments.filter((seg) => seg.value > 0).map((seg) => (
                  <Box key={seg.key} sx={{ flex: seg.value, bgcolor: seg.color, borderRadius: 999 }} />
                ))
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const AttentionCard = ({ summary, onOpen }) => {
  const inline = [
    summary.latency && `Latency ${summary.latency}`,
    summary.packetLoss && `Packet loss ${summary.packetLoss}`,
    summary.hops && `Max hops ${summary.hops}`,
  ].filter(Boolean);
  return (
    <Card
      onClick={onOpen}
      sx={{
        height: '100%',
        cursor: 'pointer',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:hover': { borderColor: NEUTRAL[300], boxShadow: CARD_SHADOW_HOVER },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 1.75 }, display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.3 }}>{summary.name}</Typography>
            <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
              {formatNumber(summary.total)} devices · {formatPct(summary.onlinePct)} online
            </Typography>
          </Box>
          <StatusChip statusKey={summary.ui.key} label={summary.ui.label} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {summary.smartTotal > 0 && (
            <DeviceRow label="Smart" connected={summary.smartConnected} total={summary.smartTotal} />
          )}
          {summary.nonSmartTotal > 0 && (
            <DeviceRow label="Non-smart" connected={summary.nonSmartConnected} total={summary.nonSmartTotal} />
          )}
        </Box>
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1, minWidth: 0 }}>
            {inline.join(' · ') || 'No network metrics'}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        </Box>
      </CardContent>
    </Card>
  );
};

const HealthyTile = ({ summary, onOpen }) => (
  <Box
    component="button"
    type="button"
    onClick={onOpen}
    sx={{
      all: 'unset',
      boxSizing: 'border-box',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: 1.75,
      py: 1.25,
      borderRadius: '8px',
      border: `1px solid ${NEUTRAL[200]}`,
      bgcolor: 'background.paper',
      cursor: 'pointer',
      transition: 'border-color 120ms ease, background-color 120ms ease',
      '&:hover': { borderColor: NEUTRAL[300], bgcolor: NEUTRAL[50] },
      '&:focus-visible': { outline: `2px solid ${NEUTRAL[400]}`, outlineOffset: 2 },
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{summary.name}</Typography>
      <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(summary.total)} devices{summary.latency ? ` · ${summary.latency}` : ''}
      </Typography>
    </Box>
  </Box>
);

const PREVIEW_LIMIT = 5;

const attentionReason = (summary) => {
  const parts = [];
  const nonSmartOffline = Math.max(0, summary.nonSmartTotal - summary.nonSmartConnected);
  if (summary.smartOffline > 0) {
    parts.push(`${formatNumber(summary.smartOffline)} of ${formatNumber(summary.smartTotal)} smart offline`);
  }
  if (nonSmartOffline > 0) {
    parts.push(`${formatNumber(nonSmartOffline)} of ${formatNumber(summary.nonSmartTotal)} non-smart offline`);
  }
  return parts.join(' · ') || summary.ui.label;
};

const OverviewAttention = ({ projects, totalProjects, onOpen, onOpenProjects }) => {
  const preview = projects.slice(0, PREVIEW_LIMIT);
  const rest = projects.length - preview.length;
  return (
    <Card>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, pt: 2, pb: 1.5 }}>
          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>Needs attention</Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {projects.length}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            onClick={onOpenProjects}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            sx={{ color: 'text.secondary' }}
          >
            All projects
          </Button>
        </Box>
        {preview.length === 0 ? (
          <Typography sx={{ px: 2.25, pb: 2.25, fontSize: '0.875rem', color: 'text.secondary' }}>
            All {formatNumber(totalProjects)} projects are healthy.
          </Typography>
        ) : (
          preview.map((summary) => (
            <Box
              key={summary.id}
              component="button"
              type="button"
              onClick={() => onOpen(summary.id)}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2.25,
                py: 1.25,
                cursor: 'pointer',
                borderTop: `1px solid ${NEUTRAL[200]}`,
                '&:hover': { bgcolor: NEUTRAL[50] },
                '&:focus-visible': { outline: `2px solid ${NEUTRAL[400]}`, outlineOffset: -2 },
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: summary.ui.color, flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 500, flex: '1 1 28%', minWidth: 0 }}>
                {summary.name}
              </Typography>
              <Typography noWrap sx={{ fontSize: '0.8125rem', color: 'text.secondary', flex: '1 1 42%', minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
                {attentionReason(summary)}
              </Typography>
              <StatusChip statusKey={summary.ui.key} label={summary.ui.label} />
            </Box>
          ))
        )}
        {rest > 0 && (
          <Box sx={{ px: 1.25, py: 1, borderTop: `1px solid ${NEUTRAL[200]}` }}>
            <Button size="small" onClick={onOpenProjects} sx={{ color: 'text.secondary' }}>
              {rest} more in Projects
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * ProjectsNetworkStatusTab Component
 * 
 * Displays a GLOBAL aggregated view of network status across ALL projects.
 * Data is fetched from GET /factory/network-status endpoint.
 * 
 * API Response Structure:
 * {
 *   "total_projects": number,
 *   "projects": {
 *     "<project_id>": {
 *       "project_id": string,
 *       "devices_by_type": {
 *         "<device_type>": {
 *           "connected": number,
 *           "offline": number
 *         }
 *       },
 *       "topology_metrics": {
 *         "max_tree_depth_connected"?: number,
 *         "max_connected_nodes_per_cap"?: number
 *       },
 *       "network_quality_metrics": {
 *         "avg_latency_quality": number
 *       },
 *       "software_versions": {
 *         "<device_type>": {
 *           "<version>": number
 *         }
 *       }
 *     }
 *   }
 * }
 */
const ProjectsNetworkStatusTab = ({ isActive, demoMode, section = 'projects', onOpenProjects }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [lmsData, setLmsData] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards', 'map', or 'list'
  const [selectedProjectId, setSelectedProjectId] = useState(null); // Track selected project for details dialog
  const [selectedProjectData, setSelectedProjectData] = useState(null); // Cache merged project data to avoid refetch in dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false); // Track details dialog open state
  const [breakdownAnchor, setBreakdownAnchor] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Search query for filtering projects
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'attention' | 'healthy'
  const [expandedListRow, setExpandedListRow] = useState(null); // Track expanded list row (only one at a time)
  const [sortField, setSortField] = useState(null); // Current sort field: 'status', 'project', etc.
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [lastUpdated, setLastUpdated] = useState(null);
  const [detailsDialogTabControl, setDetailsDialogTabControl] = useState(null);

  // If demo mode is enabled, start in Google Map view
  useEffect(() => {
    if (!demoMode) return;
    setViewMode('map');
  }, [demoMode]);

  /**
   * Fetch network status and LMS status
   * Network data: GET /factory/network-status (SMART devices)
   * LMS data: GET /factory/projectsstats (NON-SMART devices)
   * These are fetched in parallel and merged by project ID
   */
  const fetchNetworkStatus = useCallback(async ({ signal } = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both network and LMS data in parallel
      const [networkResult, lmsResult] = await Promise.allSettled([
        api.getFactoryNetworkStatus(null, { signal }),
        api.getFactoryLmsStatus(null, { signal }),
      ]);

      if (networkResult.status === 'fulfilled') {
        setNetworkData(networkResult.value);
      } else {
        if (!networkResult.reason?.isAbort) {
          console.error('Error fetching network status:', networkResult.reason);
          setError(networkResult.reason?.message || 'Failed to fetch network status');
        }
      }

      if (lmsResult.status === 'fulfilled') {
        setLmsData(lmsResult.value);
      } else {
        if (!lmsResult.reason?.isAbort) {
          console.warn('Error fetching LMS status:', lmsResult.reason);
          setLmsData(null);
          setError(lmsResult.reason?.message || 'Failed to fetch LMS status');
        }
      }

      setLastUpdated(new Date());
    } catch (err) {
      if (!err?.isAbort) {
        console.error('Error fetching factory status:', err);
        setError(err.message || 'Failed to fetch status data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data when tab becomes active
  useEffect(() => {
    if (isActive && !networkData) {
      const controller = new AbortController();
      fetchNetworkStatus({ signal: controller.signal });
      return () => controller.abort();
    }
    return undefined;
  }, [isActive, networkData, fetchNetworkStatus]);

  /**
   * Merge network data with LMS data by project ID
   * Returns network data with lms_data injected into matching projects
   */
  const getMergedProjectsData = useCallback(() => {
    if (!networkData || !networkData.projects) {
      return null;
    }

    // If no LMS data, return network data as-is
    if (!lmsData || !lmsData.projects) {
      return networkData;
    }

    // Create a map of LMS data by project_id (normalized to lowercase)
    const lmsMap = {};
    if (Array.isArray(lmsData.projects)) {
      lmsData.projects.forEach((lmsProject) => {
        if (lmsProject?.project_id) {
          const normalizedId = lmsProject.project_id.toLowerCase();
          lmsMap[normalizedId] = lmsProject;
        }
      });
    }

    // Merge LMS data into network projects
    const mergedProjects = {};
    Object.entries(networkData.projects).forEach(([projectId, project]) => {
      const normalizedId = (project.project_id || projectId).toLowerCase();
      const lmsProjectData = lmsMap[normalizedId];
      
      mergedProjects[projectId] = {
        ...project,
        lms_data: lmsProjectData || null,
      };
    });

    return {
      ...networkData,
      projects: mergedProjects,
    };
  }, [networkData, lmsData]);

  // Register demo mode controls and expose projects list
  useEffect(() => {
    if (!demoMode) return;

    const merged = getMergedProjectsData();
    const projectIds = merged?.projects ? Object.keys(merged.projects) : [];
    // Filter out unassigned-devices project from demo
    const demoProjectIds = projectIds.filter(id => 
      !id.toLowerCase().includes('unassigned-devices')
    );
    window.__demoProjects = demoProjectIds;

    demoMode.registerControls('projectsTab', {
      setViewMode,
      handleViewProjectDetails: (projectId) => {
        setSelectedProjectId(projectId);
        const mergedForLookup = getMergedProjectsData();
        const norm = String(projectId || '').toLowerCase();
        const projectEntry =
          Object.values(mergedForLookup?.projects || {}).find(
            (p) => (p?.project_id || '').toLowerCase() === norm
          ) || null;
        setSelectedProjectData(projectEntry);
        setDetailsDialogOpen(true);
      },
      closeProjectDetails: () => {
        setDetailsDialogOpen(false);
        setSelectedProjectId(null);
        setSelectedProjectData(null);
      },
      ...(detailsDialogTabControl || {}),
    });
  }, [demoMode, getMergedProjectsData, detailsDialogTabControl]);

  /**
   * Calculate aggregated metrics from merged data
   * Sums across all projects and device types
   * Also aggregates by device type for summary display
   * 
   * SMART vs NON-SMART:
   * - SMART: devices_by_type (CAPs, Gateways, AI, etc with network telemetry)
   * - NON-SMART: LMS data (lamps without network telemetry)
   */
  const calculateAggregatedMetrics = () => {
    const mergedData = getMergedProjectsData();
    
    if (!mergedData || !mergedData.projects) {
      return {
        totalProjects: 0,
        totalConnected: 0,
        totalOffline: 0,
        devicesByType: {},
        nonSmartTotal: 0,
        nonSmartOnline: 0,
        nonSmartOffline: 0,
      };
    }

    let totalConnected = 0;
    let totalOffline = 0;
    const devicesByType = {};
    
    // NON-SMART device counters (from LMS data)
    let nonSmartTotal = 0;
    let nonSmartOnline = 0;
    let nonSmartOffline = 0;

    // Iterate over all projects
    Object.values(mergedData.projects).forEach((project) => {
      // SMART devices (devices_by_type) - only count SMART device types
      if (project.devices_by_type) {
        // Iterate over all device types in this project
        Object.entries(project.devices_by_type).forEach(([deviceType, deviceTypeData]) => {
          // Only count SMART devices
          if (isSmartDeviceType(deviceType)) {
            const connected = deviceTypeData.connected || 0;
            const offline = deviceTypeData.offline || 0;
            
            totalConnected += connected;
            totalOffline += offline;
            
            // Aggregate by device type (normalized name)
            const normalizedName = normalizeDeviceTypeName(deviceType);
            if (!devicesByType[normalizedName]) {
              devicesByType[normalizedName] = { connected: 0, offline: 0 };
            }
            devicesByType[normalizedName].connected += connected;
            devicesByType[normalizedName].offline += offline;
          }
        });
      }
      
      // NON-SMART devices (LMS data - gateways and lighting assets)
      if (project.lms_data) {
        // Gateways
        const gateways = project.lms_data.active_gateways || 0;
        nonSmartTotal += gateways;
        nonSmartOnline += gateways; // Gateways are considered online if active
        
        // Lights
        const lampsTotal = project.lms_data.lamps_total || 0;
        const lampsOnline = project.lms_data.lamps_online || 0;
        const lampsOffline = project.lms_data.lamps_offline || 0;
        
        nonSmartTotal += lampsTotal;
        nonSmartOnline += lampsOnline;
        nonSmartOffline += lampsOffline;
      }
    });

    // Calculate totals
    const smartTotal = totalConnected + totalOffline;
    const nonSmartTotalCalc = nonSmartTotal;

    return {
      totalProjects: mergedData.total_projects || 0,
      totalConnected,
      totalOffline,
      devicesByType,
      smartTotal,
      nonSmartTotal: nonSmartTotalCalc,
      nonSmartOnline,
      nonSmartOffline,
    };
  };

  const aggregatedMetrics = calculateAggregatedMetrics();
  const mergedData = getMergedProjectsData();

  // Calculate SMART breakdown for TOTAL SMART card
  const smartBreakdown = useMemo(() => {
    if (!mergedData || !mergedData.projects) return [];
    const typeMap = {};
    
    Object.values(mergedData.projects).forEach((project) => {
      if (project.devices_by_type) {
        Object.entries(project.devices_by_type).forEach(([deviceType, deviceTypeData]) => {
          if (isSmartDeviceType(deviceType)) {
            const normalizedName = normalizeDeviceTypeName(deviceType);
            if (!typeMap[normalizedName]) {
              typeMap[normalizedName] = { connected: 0, disconnected: 0 };
            }
            typeMap[normalizedName].connected += deviceTypeData.connected || 0;
            typeMap[normalizedName].disconnected += deviceTypeData.offline || 0;
          }
        });
      }
    });

    return Object.entries(typeMap)
      .filter(([_, counts]) => counts.connected > 0 || counts.disconnected > 0)
      .map(([type, counts]) => ({
        type,
        connected: counts.connected,
        disconnected: counts.disconnected
      }))
      .sort((a, b) => a.type.localeCompare(b.type));
  }, [mergedData]);

  // Filter merged data based on search query ONLY
  // IMPORTANT: Do NOT filter by status, health, or disconnected count
  // All projects (Critical, Healthy, Degraded) must be included
  const filteredMergedData = useMemo(() => {
    if (!mergedData || !mergedData.projects) return mergedData;
    if (!searchQuery.trim()) return mergedData;

    const query = searchQuery.toLowerCase().trim();
    const filteredProjects = {};

    Object.entries(mergedData.projects).forEach(([projectId, project]) => {
      const projectIdLower = (projectId || '').toLowerCase();
      const projectNameLower = (project.project_name || '').toLowerCase();
      const locationLower = (project.location || '').toLowerCase();
      
      if (
        projectIdLower.includes(query) ||
        projectNameLower.includes(query) ||
        locationLower.includes(query)
      ) {
        filteredProjects[projectId] = project;
      }
    });

    return {
      ...mergedData,
      projects: filteredProjects
    };
  }, [mergedData, searchQuery]);

  // Optional status filter (UI-only). Maps internal health "degraded" -> "warning"
  const statusFilteredMergedData = useMemo(() => {
    if (!filteredMergedData?.projects) return filteredMergedData;
    if (statusFilter === 'all') return filteredMergedData;

    const filteredProjects = {};
    Object.entries(filteredMergedData.projects).forEach(([projectId, project]) => {
      const ui = mapHealthToUiStatus(classifyHealth(computeProjectStats(project)));
      const isHealthy = ui.key === 'healthy';
      if ((statusFilter === 'healthy') === isHealthy) {
        filteredProjects[projectId] = project;
      }
    });

    return {
      ...filteredMergedData,
      projects: filteredProjects,
    };
  }, [filteredMergedData, statusFilter]);

  // Sort projects based on sortField and sortDirection
  // IMPORTANT: Always return ALL projects, regardless of status
  const sortedProjects = useMemo(() => {
    if (!statusFilteredMergedData?.projects) {
      return {};
    }

    // If no sort field is selected, return all projects as-is
    if (!sortField) {
      return statusFilteredMergedData.projects;
    }

    const projectsArray = Object.entries(statusFilteredMergedData.projects);

    const getSortNumeric = (projectId, project) => {
      if (sortField === 'status') {
        const stats = computeProjectStats(project);
        const health = classifyHealth(stats);
        // Status priority: Critical (0) > Degraded (1) > Healthy (2)
        const statusRank = (status) => {
          if (status === 'critical') return 0;
          if (status === 'degraded') return 1;
          if (status === 'healthy') return 2;
          return 3;
        };
        return statusRank(health.status);
      }

      if (sortField === 'project') {
        // handled as string compare below
        return 0;
      }

      if (sortField === 'latency') {
        const metrics = project.network_quality_metrics || {};
        const avgLatencyMs = metrics.avg_latency;
        const avgLatencyQuality = metrics.avg_latency_quality;
        // Prefer milliseconds when available; otherwise approximate via (1 - quality) scale
        if (typeof avgLatencyMs === 'number') return avgLatencyMs;
        if (typeof avgLatencyQuality === 'number') return 1 - avgLatencyQuality;
        return Number.POSITIVE_INFINITY;
      }

      if (sortField === 'packetLoss') {
        const metrics = project.network_quality_metrics || {};
        const packetLoss = metrics.avg_packet_loss_pct ?? metrics.avg_packet_loss ?? metrics.packet_loss_percent;
        return packetLoss !== null && packetLoss !== undefined ? Number(packetLoss) : Number.POSITIVE_INFINITY;
      }

      if (sortField === 'smartDevices' || sortField === 'nonSmartDevices') {
        const { smart, nonSmart } = getAllDeviceTypes(project);
        const smartConnected = smart.reduce((sum, d) => sum + (d.connected || 0), 0);
        const smartTotal = smart.reduce((sum, d) => sum + (d.total || 0), 0);
        const smartDisconnected = Math.max(0, smartTotal - smartConnected);

        const nonSmartConnected = nonSmart.reduce((sum, d) => sum + (d.connected || 0), 0);
        const nonSmartTotal = nonSmart.reduce((sum, d) => sum + (d.total || 0), 0);
        const nonSmartDisconnected = Math.max(0, nonSmartTotal - nonSmartConnected);

        // Sort by disconnected first (more urgent), then by total
        if (sortField === 'smartDevices') return smartDisconnected * 1_000_000 + smartTotal;
        return nonSmartDisconnected * 1_000_000 + nonSmartTotal;
      }

      return 0;
    };
    
    // Sort all projects (including Healthy ones)
    const sorted = [...projectsArray].sort(([projectIdA, projectA], [projectIdB, projectB]) => {
      if (sortField === 'project') {
        return sortDirection === 'asc'
          ? projectIdA.localeCompare(projectIdB)
          : projectIdB.localeCompare(projectIdA);
      }

      const a = getSortNumeric(projectIdA, projectA);
      const b = getSortNumeric(projectIdB, projectB);
      if (a !== b) return sortDirection === 'asc' ? a - b : b - a;

      // Stable fallback
      return projectIdA.localeCompare(projectIdB);
    });

    return Object.fromEntries(sorted);
  }, [statusFilteredMergedData, sortField, sortDirection]);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const healthCounts = useMemo(() => {
    const counts = { total: 0, critical: 0, warning: 0, healthy: 0 };
    Object.values(mergedData?.projects || {}).forEach((project) => {
      const ui = mapHealthToUiStatus(classifyHealth(computeProjectStats(project)));
      counts.total += 1;
      counts[ui.key] = (counts[ui.key] || 0) + 1;
    });
    return counts;
  }, [mergedData]);

  // Counts after search (before the status filter), used by the filter tabs.
  const searchHealthCounts = useMemo(() => {
    const counts = { all: 0, attention: 0, healthy: 0 };
    Object.values(filteredMergedData?.projects || {}).forEach((project) => {
      const ui = mapHealthToUiStatus(classifyHealth(computeProjectStats(project)));
      counts.all += 1;
      counts[ui.key === 'healthy' ? 'healthy' : 'attention'] += 1;
    });
    return counts;
  }, [filteredMergedData]);

  const projectSummaries = useMemo(() => {
    const list = Object.entries(sortedProjects || {}).map(([id, project]) => summarizeProject(id, project));
    const attention = list.filter((p) => p.ui.key !== 'healthy');
    const healthy = list.filter((p) => p.ui.key === 'healthy');
    if (!sortField) {
      attention.sort((a, b) => (b.smartOffline / (b.smartTotal || 1)) - (a.smartOffline / (a.smartTotal || 1)) || a.name.localeCompare(b.name));
      healthy.sort((a, b) => a.name.localeCompare(b.name));
    }
    return { attention, healthy };
  }, [sortedProjects, sortField]);

  const overviewAttention = useMemo(() => {
    return Object.entries(mergedData?.projects || {})
      .map(([id, project]) => summarizeProject(id, project))
      .filter((project) => project.ui.key !== 'healthy')
      .sort((a, b) => (b.smartOffline / (b.smartTotal || 1)) - (a.smartOffline / (a.smartTotal || 1)) || a.name.localeCompare(b.name));
  }, [mergedData]);


  // Handle loading state
  if (loading && !networkData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle error state
  if (error) {
    const isAuthError = error.includes('No authentication token') || error.includes('authentication token');
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        {isAuthError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Authentication Required
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Please set your JWT token in the browser console:
            </Typography>
            <Box component="pre" sx={{ 
              bgcolor: NEUTRAL[100], 
              p: 1.5, 
              borderRadius: 1,
              fontSize: '0.85rem',
              overflow: 'auto',
              mb: 1
            }}>
{`localStorage.setItem('token', 'YOUR_JWT_TOKEN_HERE');
location.reload();`}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Tip: If you're logged into the main Network Manager UI, you can copy the token from there.
            </Typography>
          </Alert>
        )}
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchNetworkStatus}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Handle empty state
  if (!mergedData || !mergedData.projects || Object.keys(mergedData.projects).length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No project data available.</Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchNetworkStatus}
          sx={{ mt: 2 }}
        >
          Refresh
        </Button>
      </Box>
    );
  }


  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };


  // Handle list row expansion (only one row expanded at a time)
  const toggleListRowExpansion = (projectId) => {
    setExpandedListRow(prev => prev === projectId ? null : projectId);
  };

  // Handle view project details
  const handleViewProjectDetails = (projectId) => {
    setSelectedProjectId(projectId);
    // Pass the already-merged project object into the dialog so it doesn't refetch.
    // Match by normalized project_id because the map/list may provide different casing/keys.
    const norm = String(projectId || '').toLowerCase();
    const projectEntry =
      Object.values(mergedData?.projects || {}).find(
        (p) => (p?.project_id || '').toLowerCase() === norm
      ) || null;
    setSelectedProjectData(projectEntry);
    setDetailsDialogOpen(true);
  };

  // Handle close details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedProjectId(null);
    setSelectedProjectData(null);
  };

  return (
    <Box sx={{ 
      p: 0,
      position: 'relative',
      height: 'auto',
      overflow: 'visible',
    }}>
      {section === 'projects' && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          placeholder="Search projects"
          sx={{ width: { xs: '100%', sm: 300 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          size="small"
          aria-label="Filter projects by status"
          onChange={(_e, v) => v && setStatusFilter(v)}
          sx={{ '& .MuiToggleButton-root': { px: 1.5, gap: 0.75, textTransform: 'none', fontSize: '0.8125rem' } }}
        >
          <ToggleButton value="attention">
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: HEADER_BAR.critical }} />
            Needs attention
            <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{searchHealthCounts.attention}</Box>
          </ToggleButton>
          <ToggleButton value="healthy">
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: HEADER_BAR.healthy }} />
            Healthy
            <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{searchHealthCounts.healthy}</Box>
          </ToggleButton>
          <ToggleButton value="all">
            All
            <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{searchHealthCounts.all}</Box>
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', display: { xs: 'none', lg: 'block' } }}>
          Updated {formatUtcTime(lastUpdated)}
        </Typography>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="cards" aria-label="cards view">
            <ViewModuleIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ListIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
          <ToggleButton value="map" aria-label="map view">
            <MapIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={fetchNetworkStatus}
              disabled={loading}
              sx={{ border: 1, borderColor: 'grey.300', bgcolor: 'background.paper', width: 36, height: 36 }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      )}

      {section === 'overview' && (
      <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
          Updated {formatUtcTime(lastUpdated)}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={fetchNetworkStatus}
              disabled={loading}
              sx={{ border: 1, borderColor: 'grey.300', bgcolor: 'background.paper', width: 36, height: 36 }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Summary */}
      {(() => {
        const smartTotal = aggregatedMetrics.smartTotal || 0;
        const smartConnected = aggregatedMetrics.totalConnected || 0;
        const smartOffline = aggregatedMetrics.totalOffline || 0;
        const nonSmartTotal = aggregatedMetrics.nonSmartTotal || 0;
        const nonSmartOnline = aggregatedMetrics.nonSmartOnline || 0;
        const nonSmartOffline = aggregatedMetrics.nonSmartOffline || 0;
        const pct = (online, total) => (total > 0 ? (online / total) * 100 : null);
        const detailText = (text, tone) => (
          <Typography component="span" sx={{ fontSize: '0.8125rem', color: tone ? textFor(tone) : 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {text}
          </Typography>
        );
        const byTypeButton = (
          <Button
            size="small"
            onClick={(e) => setBreakdownAnchor(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{ mr: -1, minHeight: 0, py: 0.25, color: 'text.secondary', fontSize: '0.75rem' }}
          >
            By type
          </Button>
        );

        const attention = healthCounts.critical + healthCounts.warning;

        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <StatTile
                icon={GridViewOutlinedIcon}
                label="Projects"
                value={formatNumber(healthCounts.total)}
                detail={attention > 0
                  ? detailText(`${attention} need attention`, STATUS.critical)
                  : detailText('All healthy')}
                segments={[
                  { key: 'attention', value: attention, color: HEADER_BAR.critical },
                  { key: 'healthy', value: healthCounts.healthy, color: HEADER_BAR.healthy },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatTile
                icon={RouterOutlinedIcon}
                label="Smart devices"
                value={`${formatPct(pct(smartConnected, smartTotal))} online`}
                detail={detailText(`${formatNumber(smartOffline)} of ${formatNumber(smartTotal)} offline`, smartOffline > 0 ? STATUS.critical : null)}
                segments={[
                  { key: 'online', value: smartConnected, color: HEADER_BAR.healthy },
                  { key: 'offline', value: smartOffline, color: HEADER_BAR.critical },
                ]}
                action={byTypeButton}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatTile
                icon={LightbulbOutlinedIcon}
                label="Non-smart devices"
                value={`${formatPct(pct(nonSmartOnline, nonSmartTotal))} online`}
                detail={detailText(`${formatNumber(nonSmartOffline)} of ${formatNumber(nonSmartTotal)} offline`, nonSmartOffline > 0 ? STATUS.critical : null)}
                segments={[
                  { key: 'online', value: nonSmartOnline, color: HEADER_BAR.healthy },
                  { key: 'offline', value: nonSmartOffline, color: HEADER_BAR.critical },
                ]}
              />
            </Grid>
          </Grid>
        );
      })()}
      <Popover
        open={Boolean(breakdownAnchor)}
        anchorEl={breakdownAnchor}
        onClose={() => setBreakdownAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, width: 300 } }}
      >
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, mb: 1 }}>
          Smart devices by type
        </Typography>
        <BreakdownList rows={smartBreakdown} emptyLabel="No smart devices" />
      </Popover>

      <OverviewAttention
        projects={overviewAttention}
        totalProjects={healthCounts.total}
        onOpen={handleViewProjectDetails}
        onOpenProjects={onOpenProjects}
      />
      </>
      )}

      {section === 'projects' && (
      <>
      {/* View Content - Cards, Map, or List */}
      {viewMode === 'map' ? (
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            // Give the map a stable size so it can’t collapse on resize,
            // and so the page remains scrollable.
            height: { xs: 320, sm: 420, md: 520, lg: 600 },
            borderRadius: '10px',
            overflow: 'hidden',
            border: `1px solid ${NEUTRAL[200]}`,
            bgcolor: 'background.paper',
            boxShadow: CARD_SHADOW,
          }}
        >
          <ProjectsNetworkStatusMapView
            networkData={statusFilteredMergedData}
            isActive={viewMode === 'map'}
            onViewProjectDetails={handleViewProjectDetails}
            demoMode={demoMode}
          />
        </Box>
      ) : viewMode === 'list' ? (
        <>
          {isMobile ? (
            /* Mobile Card Layout */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Render ALL projects - no filtering by status, health, or disconnected count */}
              {sortedProjects && Object.entries(sortedProjects).map(([projectId, project]) => {
                const stats = computeProjectStats(project);
                const health = classifyHealth(stats);
                const { smart, nonSmart } = getAllDeviceTypes(project);
                
                // Calculate SMART summary
                const smartConnected = smart.reduce((sum, d) => sum + (d.connected || 0), 0);
                const smartDisconnected = smart.reduce((sum, d) => sum + ((d.total || 0) - (d.connected || 0)), 0);
                
                // Calculate NON-SMART summary
                const nonSmartConnected = nonSmart.reduce((sum, d) => sum + (d.connected || 0), 0);
                const nonSmartDisconnected = nonSmart.reduce((sum, d) => sum + ((d.total || 0) - (d.connected || 0)), 0);
                
                const isExpanded = expandedListRow === projectId;
                
                // Get network quality metrics (same as Cards view)
                const metrics = project.network_quality_metrics || {};
                const topology = project.topology_metrics || {};
                const networkParts = [];
                
                // Avg Latency (from avg_latency_quality - 0-1 quality score)
                const avgLatencyQuality = metrics.avg_latency_quality;
                if (avgLatencyQuality !== null && avgLatencyQuality !== undefined && typeof avgLatencyQuality === 'number') {
                  networkParts.push({ label: 'Latency', value: `${avgLatencyQuality.toFixed(3)}` });
                }
                
                // Packet Loss (%)
                const packetLoss = metrics.avg_packet_loss_pct ?? metrics.avg_packet_loss ?? metrics.packet_loss_percent;
                if (packetLoss !== null && packetLoss !== undefined) {
                  networkParts.push({ label: 'Loss', value: `${Number(packetLoss).toFixed(2)}%` });
                }
                
                // Max Tree Depth (hops)
                const maxTreeDepth = topology.max_tree_depth_connected ?? topology.max_tree_depth ?? topology.max_hops;
                if (maxTreeDepth !== null && maxTreeDepth !== undefined) {
                  networkParts.push({ label: 'Depth', value: `${maxTreeDepth} hops` });
                }
                
                return (
                  <Card key={projectId} sx={{ cursor: 'pointer' }} onClick={() => toggleListRowExpansion(projectId)}>
                    <CardContent>
                      {/* Project Name + Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {project.project_id || projectId}
                        </Typography>
                        <Chip
                          label={health.label}
                          size="small"
                          sx={{
                            bgcolor: statusTint(health.color),
                            color: textFor(health.color),
                            fontWeight: 500,
                            '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', bgcolor: health.color, ml: 1 },
                          }}
                        />
                      </Box>
                      
                      {/* SMART Summary */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          SMART
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Box component="span" sx={{ color: 'success.main' }}>{smartConnected} Connected</Box> · <Box component="span" sx={{ color: 'error.main' }}>{smartDisconnected} Disconnected</Box>
                        </Typography>
                      </Box>
                      
                      {/* NON-SMART Summary */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          NON-SMART
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Box component="span" sx={{ color: 'success.main' }}>{nonSmartConnected} Connected</Box> · <Box component="span" sx={{ color: 'error.main' }}>{nonSmartDisconnected} Disconnected</Box>
                        </Typography>
                      </Box>
                      
                      {/* Network Quality */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          NETWORK QUALITY
                        </Typography>
                        {networkParts.length > 0 ? (
                          <Typography variant="body2" component="span">
                            {networkParts.map((part, index) => (
                              <React.Fragment key={part.label}>
                                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                  {part.label}:
                                </Box>
                                {' '}
                                <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                  {part.value}
                                </Box>
                                {index < networkParts.length - 1 && (
                                  <Box component="span" sx={{ mx: 1, color: 'text.secondary' }}>
                                    |
                                  </Box>
                                )}
                              </React.Fragment>
                            ))}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Action Button */}
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewProjectDetails(projectId);
                        }}
                        sx={{ mt: 1 }}
                      >
                        View Details
                      </Button>
                      
                      {/* Expanded Details */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                          {/* SMART Section */}
                          <Box sx={{ mb: 3 }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 700, 
                                mb: 1.5, 
                                letterSpacing: '0.06em',
                                fontSize: '0.95rem'
                              }}
                            >
                              SMART
                            </Typography>
                            <Divider sx={{ mb: 1.5 }} />
                            {smart.filter(d => d.total > 0).length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {/* Header Row */}
                                <Box sx={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: '120px 1fr 1fr',
                                  gap: 2,
                                  mb: 0.5,
                                  pb: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider'
                                }}>
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    TYPE
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                                    CONNECTED
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                                    DISCONNECTED
                                  </Typography>
                                </Box>
                                {/* Data Rows */}
                                {smart.filter(d => d.total > 0).map((deviceInfo) => {
                                  const offline = (deviceInfo.total || 0) - (deviceInfo.connected || 0);
                                  return (
                                    <Box 
                                      key={deviceInfo.displayName} 
                                      sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '120px 1fr 1fr',
                                        gap: 2,
                                        alignItems: 'center'
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                        {deviceInfo.displayName}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 400, textAlign: 'center' }}>
                                        {deviceInfo.connected || 0}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 400, textAlign: 'center' }}>
                                        {offline}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No SMART devices
                              </Typography>
                            )}
                          </Box>
                          {/* NON-SMART Section */}
                          {nonSmart.filter(d => d.total > 0).length > 0 && (
                            <Box sx={{ pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 700, 
                                  mb: 1.5, 
                                  letterSpacing: '0.06em',
                                  fontSize: '0.95rem'
                                }}
                              >
                                NON-SMART
                              </Typography>
                              <Divider sx={{ mb: 1.5 }} />
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {/* Header Row */}
                                <Box sx={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: '120px 1fr 1fr',
                                  gap: 2,
                                  mb: 0.5,
                                  pb: 0.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider'
                                }}>
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    TYPE
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                                    CONNECTED
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                                    DISCONNECTED
                                  </Typography>
                                </Box>
                                {/* Data Rows */}
                                {nonSmart.filter(d => d.total > 0).map((deviceInfo) => {
                                  const offline = (deviceInfo.total || 0) - (deviceInfo.connected || 0);
                                  return (
                                    <Box 
                                      key={deviceInfo.displayName} 
                                      sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '120px 1fr 1fr',
                                        gap: 2,
                                        alignItems: 'center'
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                        {deviceInfo.displayName}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 400, textAlign: 'center' }}>
                                        {deviceInfo.connected || 0}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 400, textAlign: 'center' }}>
                                        {offline}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            /* Desktop Table Layout */
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '10px',
                border: `1px solid ${NEUTRAL[200]}`,
                bgcolor: 'background.paper',
                boxShadow: CARD_SHADOW,
                overflow: 'hidden',
              }}
            >
            <Table sx={{ '& td, & th': { borderColor: 'divider' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: NEUTRAL[50] }}>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.70rem', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                      Project Name
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortField === 'status' ? 1 : 0.3 } }}
                    >
                      <Typography sx={{ fontSize: '0.70rem', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                        Status
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'smartDevices'}
                      direction={sortField === 'smartDevices' ? sortDirection : 'asc'}
                      onClick={() => handleSort('smartDevices')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortField === 'smartDevices' ? 1 : 0.3 } }}
                    >
                      <Typography sx={{ fontSize: '0.70rem', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                        Smart Devices
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'nonSmartDevices'}
                      direction={sortField === 'nonSmartDevices' ? sortDirection : 'asc'}
                      onClick={() => handleSort('nonSmartDevices')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortField === 'nonSmartDevices' ? 1 : 0.3 } }}
                    >
                      <Typography sx={{ fontSize: '0.70rem', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                        Non-Smart Devices
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'latency'}
                      direction={sortField === 'latency' ? sortDirection : 'asc'}
                      onClick={() => handleSort('latency')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortField === 'latency' ? 1 : 0.3 } }}
                    >
                      <Typography sx={{ fontSize: '0.70rem', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                        Latency
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'packetLoss'}
                      direction={sortField === 'packetLoss' ? sortDirection : 'asc'}
                      onClick={() => handleSort('packetLoss')}
                      sx={{ '& .MuiTableSortLabel-icon': { opacity: sortField === 'packetLoss' ? 1 : 0.3 } }}
                    >
                      <Typography sx={{ fontSize: '0.70rem', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                        Packet Loss
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.70rem', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                      Actions
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Render ALL projects - no filtering by status, health, or disconnected count */}
                {sortedProjects && Object.entries(sortedProjects).map(([projectId, project]) => {
                  const stats = computeProjectStats(project);
                  const health = classifyHealth(stats);
                  const ui = mapHealthToUiStatus(health);
                  const { smart, nonSmart } = getAllDeviceTypes(project);
                  
                  // Calculate SMART summary
                  const smartConnected = smart.reduce((sum, d) => sum + (d.connected || 0), 0);
                  const smartTotal = smart.reduce((sum, d) => sum + (d.total || 0), 0);
                  const smartDisconnected = Math.max(0, smartTotal - smartConnected);
                  
                  // Calculate NON-SMART summary
                  const nonSmartConnected = nonSmart.reduce((sum, d) => sum + (d.connected || 0), 0);
                  const nonSmartTotal = nonSmart.reduce((sum, d) => sum + (d.total || 0), 0);
                  const nonSmartDisconnected = Math.max(0, nonSmartTotal - nonSmartConnected);
                  
                  const isExpanded = expandedListRow === projectId;
                  
                  // Get network quality metrics (same as Cards view)
                  const metrics = project.network_quality_metrics || {};

                  const avgLatencyMs = metrics.avg_latency ?? null;
                  const avgLatencyQuality = metrics.avg_latency_quality ?? null;
                  const latencyValue = typeof avgLatencyMs === 'number'
                    ? `${avgLatencyMs.toFixed(3)}ms`
                    : (typeof avgLatencyQuality === 'number' ? avgLatencyQuality.toFixed(3) : '—');

                  // Packet Loss (%)
                  const packetLoss = metrics.avg_packet_loss_pct ?? metrics.avg_packet_loss ?? metrics.packet_loss_percent;
                  const packetLossValue = packetLoss !== null && packetLoss !== undefined ? `${Number(packetLoss).toFixed(2)}%` : '—';
                  
                  return (
                    <React.Fragment key={projectId}>
                      <TableRow 
                        hover 
                        onClick={() => toggleListRowExpansion(projectId)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: NEUTRAL[50] } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {project.project_id || projectId}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ui.label}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              bgcolor: statusTint(ui.color),
                              color: textFor(ui.color),
                              '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', bgcolor: ui.color, ml: 1 },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: textFor(STATUS.healthy) }} />
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
                                {formatNumber(smartConnected)}/{formatNumber(smartTotal)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              <CancelIcon sx={{ fontSize: 16, color: textFor(STATUS.critical) }} />
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
                                {formatNumber(smartDisconnected)}/{formatNumber(smartTotal)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: textFor(STATUS.healthy) }} />
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
                                {formatNumber(nonSmartConnected)}/{formatNumber(nonSmartTotal)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              <CancelIcon sx={{ fontSize: 16, color: textFor(STATUS.critical) }} />
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
                                {formatNumber(nonSmartDisconnected)}/{formatNumber(nonSmartTotal)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <WifiIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>
                              {latencyValue}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <SignalCellularAltIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>
                              {packetLossValue}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProjectDetails(projectId);
                            }}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 3, backgroundColor: 'background.default' }}>
                            <Box sx={{ pl: 2, pr: 2 }}>
                              {/* SMART Section */}
                              <Box sx={{ mb: 3 }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 700, 
                                    mb: 1.5, 
                                    letterSpacing: '0.06em',
                                    fontSize: '0.95rem'
                                  }}
                                >
                                  SMART
                                </Typography>
                                <Divider sx={{ mb: 1.5 }} />
                                {smart.filter(d => d.total > 0).length > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Header Row */}
                                    <Box sx={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: '120px 1fr 1fr',
                                      gap: 2,
                                      mb: 0.5,
                                      pb: 0.5,
                                      borderBottom: '1px solid',
                                      borderColor: 'divider'
                                    }}>
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                        TYPE
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                                        CONNECTED
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                                        DISCONNECTED
                                      </Typography>
                                    </Box>
                                    {/* Data Rows */}
                                    {smart.filter(d => d.total > 0).map((deviceInfo) => {
                                      const offline = (deviceInfo.total || 0) - (deviceInfo.connected || 0);
                                      return (
                                        <Box 
                                          key={deviceInfo.displayName} 
                                          sx={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: '120px 1fr 1fr',
                                            gap: 2,
                                            alignItems: 'center'
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                            {deviceInfo.displayName}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 400, textAlign: 'center' }}>
                                            {deviceInfo.connected || 0}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 400, textAlign: 'center' }}>
                                            {offline}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    No SMART devices
                                  </Typography>
                                )}
                              </Box>
                              {/* NON-SMART Section */}
                              {nonSmart.filter(d => d.total > 0).length > 0 && (
                                <Box sx={{ pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                                  <Typography 
                                    variant="subtitle1" 
                                    sx={{ 
                                      fontWeight: 700, 
                                      mb: 1.5, 
                                      letterSpacing: '0.06em',
                                      fontSize: '0.95rem'
                                    }}
                                  >
                                    NON-SMART
                                  </Typography>
                                  <Divider sx={{ mb: 1.5 }} />
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {/* Header Row */}
                                    <Box sx={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: '120px 1fr 1fr',
                                      gap: 2,
                                      mb: 0.5,
                                      pb: 0.5,
                                      borderBottom: '1px solid',
                                      borderColor: 'divider'
                                    }}>
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                        TYPE
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                                        CONNECTED
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                                        DISCONNECTED
                                      </Typography>
                                    </Box>
                                    {/* Data Rows */}
                                    {nonSmart.filter(d => d.total > 0).map((deviceInfo) => {
                                      const offline = (deviceInfo.total || 0) - (deviceInfo.connected || 0);
                                      return (
                                        <Box 
                                          key={deviceInfo.displayName} 
                                          sx={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: '120px 1fr 1fr',
                                            gap: 2,
                                            alignItems: 'center'
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                            {deviceInfo.displayName}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 400, textAlign: 'center' }}>
                                            {deviceInfo.connected || 0}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 400, textAlign: 'center' }}>
                                            {offline}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </>
      ) : (
        <>
          {/* Projects Section - Cards View */}
          {(() => {
            const { attention, healthy } = projectSummaries;
            const showAttention = statusFilter !== 'healthy';
            const showHealthy = statusFilter !== 'attention';
            if (attention.length + healthy.length === 0) {
              return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    {statusFilter === 'attention' && !searchQuery.trim()
                      ? 'All projects are healthy. Nothing needs attention right now.'
                      : 'No projects match your filters.'}
                  </Typography>
                </Paper>
              );
            }
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {showAttention && attention.length > 0 && (
                  <Box>
                    <SectionHeader title="Needs attention" count={attention.length} caption="Worst first" />
                    <Grid container spacing={2}>
                      {attention.map((summary) => (
                        <Grid item xs={12} sm={6} xl={4} key={summary.id}>
                          <AttentionCard summary={summary} onOpen={() => handleViewProjectDetails(summary.id)} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                {showHealthy && healthy.length > 0 && (
                  <Box>
                    <SectionHeader title="Healthy" count={healthy.length} caption="All smart devices online" />
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                      }}
                    >
                      {healthy.map((summary) => (
                        <HealthyTile key={summary.id} summary={summary} onOpen={() => handleViewProjectDetails(summary.id)} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </>
      )}
      </>
      )}

      {/* Project Details Dialog */}
      <ProjectDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        projectId={selectedProjectId}
        initialProject={selectedProjectData}
        demoMode={demoMode}
        onTabControlReady={setDetailsDialogTabControl}
      />
    </Box>
  );
};

export default ProjectsNetworkStatusTab;

